/**
 * Audit log types — append-only, tamper-evident record of clinic activity.
 *
 * Each row chains a sha256 of (prev_hash || canonical(entry)) so any
 * modification breaks the hash chain. Visible only to clinic owners.
 *
 * Retention: 10 years (medical industry standard).
 */
import { z } from "zod";

export const auditActionSchema = z.enum([
	"view",
	"create",
	"update",
	"delete",
	"export",
	"login",
	"login_failed",
	"logout",
	"issue",
	"cancel",
	"pay",
	"refund",
	"upload",
	"transition",
	"share",
]);
export type AuditAction = z.infer<typeof auditActionSchema>;

export interface AuditLogApi {
	id: string;
	occurred_at: string;
	user_id: string | null;
	/** Super-admin operator id when this action happened during impersonation. */
	impersonated_by: string | null;
	clinic_id: string | null;
	action: string;
	resource_type: string;
	resource_id: string | null;
	ip_address: string | null;
	user_agent: string | null;
	before_data: unknown;
	after_data: unknown;
	current_hash: string | null;
	prev_hash: string | null;
	actor_name: string | null;
	actor_email: string | null;
}

export const auditLogsListQuerySchema = z.object({
	action: z.string().min(1).max(50).optional(),
	resource_type: z.string().min(1).max(50).optional(),
	resource_id: z.string().uuid().optional(),
	user_id: z.string().uuid().optional(),
	from: z.string().datetime().optional(),
	to: z.string().datetime().optional(),
	page: z.coerce.number().int().positive().default(1),
	per_page: z.coerce.number().int().positive().max(100).default(50),
});
export type AuditLogsListQuery = z.infer<typeof auditLogsListQuerySchema>;

export interface AuditLogsListResult {
	items: AuditLogApi[];
	total: number;
	page: number;
	per_page: number;
	facets: {
		actions: string[];
		resource_types: string[];
	};
}
